import { useState } from "react";
import PortalLayout from "@/components/PortalLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MOCK_NOTICES } from "@/data/portalMockData";
import { MessageSquare, Bell, CheckCheck, Send } from "lucide-react";
import { toast } from "sonner";

const mockThreads = [
  { id: "t1", topic: "Velocity Pioneer — Engine Hours Update", tag: "Velocity Pioneer", messages: [
    { sender: "VO Ops", time: "Mar 24, 09:15", text: "Pioneer port engine reached 490 hrs. 500-hr service scheduled for April 1." },
    { sender: "Client A", time: "Mar 24, 10:30", text: "Acknowledged. Please confirm the vessel will be available for ops on April 2." },
    { sender: "VO Ops", time: "Mar 24, 11:00", text: "Confirmed — service window is 8 hours. Pioneer will be back on hire by 1800L April 1." },
  ]},
  { id: "t2", topic: "Invoice Query — INV-A-032", tag: "Commercial", messages: [
    { sender: "Client A", time: "Mar 22, 14:00", text: "Please confirm the line items on INV-A-032. The standby rate looks different from last month." },
    { sender: "VO Commercial", time: "Mar 22, 16:30", text: "The standby rate was adjusted per the contract amendment dated Feb 15. Revised schedule attached." },
  ]},
];

const MessagesNotices = () => {
  const [notices, setNotices] = useState(MOCK_NOTICES);
  const [selectedThread, setSelectedThread] = useState(mockThreads[0].id);
  const [newMessage, setNewMessage] = useState("");

  const handleMarkAllRead = () => {
    setNotices(n => n.map(x => ({ ...x, read: true })));
    toast.success("All notices marked as read");
  };

  const activeThread = mockThreads.find(t => t.id === selectedThread)!;

  return (
    <PortalLayout>
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[3px] uppercase text-[hsl(211,100%,40%)]/60 mb-1">Communication</p>
        <h1 className="text-3xl font-bold text-[hsl(215,25%,17%)]">Messages & Notices</h1>
      </div>

      <Tabs defaultValue="notices" className="space-y-4">
        <TabsList className="bg-white border border-[hsl(220,15%,90%)]">
          <TabsTrigger value="notices">Notice Board</TabsTrigger>
          <TabsTrigger value="messages">Messages</TabsTrigger>
        </TabsList>

        <TabsContent value="notices">
          <div className="flex justify-end mb-3">
            <Button variant="outline" size="sm" onClick={handleMarkAllRead}><CheckCheck className="w-3.5 h-3.5 mr-1" /> Mark All Read</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {notices.map(n => (
              <div key={n.id} className={`portal-card p-4 ${!n.read ? "border-l-4 border-[hsl(211,100%,40%)]" : ""}`}>
                <div className="flex items-start gap-2">
                  {!n.read && <span className="w-2 h-2 rounded-full bg-[hsl(211,100%,40%)] mt-1.5 flex-shrink-0" />}
                  <div>
                    <h3 className={`text-sm ${n.read ? "text-[hsl(215,12%,50%)]" : "text-[hsl(215,25%,17%)] font-bold"}`}>{n.title}</h3>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${
                        n.category === "Safety" ? "bg-red-100 text-red-700" :
                        n.category === "Operational" ? "bg-blue-100 text-blue-700" :
                        n.category === "Commercial" ? "bg-amber-100 text-amber-700" :
                        "bg-gray-100 text-gray-600"
                      }`}>{n.category}</span>
                      <span className="text-[10px] text-[hsl(215,12%,50%)]">{n.date}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="messages">
          <div className="portal-card flex min-h-[500px]">
            {/* Thread list */}
            <div className="w-[280px] border-r border-[hsl(220,15%,90%)] flex flex-col">
              <div className="p-3 border-b border-[hsl(220,15%,90%)]">
                <Button onClick={() => toast.success("Created: New Thread", { description: "New entry added." })} variant="outline" size="sm" className="w-full"><MessageSquare className="w-3.5 h-3.5 mr-1" /> New Thread</Button>
              </div>
              {mockThreads.map(t => (
                <button key={t.id} onClick={() => setSelectedThread(t.id)} className={`p-3 text-left border-b border-[hsl(220,15%,92%)] hover:bg-[hsl(220,20%,97%)] transition-colors ${selectedThread === t.id ? "bg-[hsl(220,20%,97%)]" : ""}`}>
                  <p className="text-xs font-bold text-[hsl(215,25%,17%)] truncate">{t.topic}</p>
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-[hsl(220,20%,97%)] text-[hsl(215,12%,50%)]">{t.tag}</span>
                </button>
              ))}
            </div>
            {/* Messages */}
            <div className="flex-1 flex flex-col">
              <div className="p-4 border-b border-[hsl(220,15%,90%)]">
                <h3 className="font-bold text-sm">{activeThread.topic}</h3>
              </div>
              <div className="flex-1 p-4 space-y-3 overflow-y-auto">
                {activeThread.messages.map((m, i) => (
                  <div key={i} className={`flex ${m.sender.startsWith("Client") ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[70%] rounded-lg p-3 ${m.sender.startsWith("Client") ? "bg-[hsl(211,100%,40%)]/10" : "bg-[hsl(220,20%,97%)]"}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-bold text-[hsl(215,25%,17%)]">{m.sender}</span>
                        <span className="text-[9px] text-[hsl(215,12%,50%)]">{m.time}</span>
                      </div>
                      <p className="text-xs text-[hsl(215,25%,17%)]">{m.text}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-3 border-t border-[hsl(220,15%,90%)] flex gap-2">
                <Input placeholder="Type a message..." value={newMessage} onChange={e => setNewMessage(e.target.value)} className="bg-[hsl(220,20%,97%)]" />
                <Button size="sm" className="bg-[hsl(211,100%,40%)] text-white" onClick={() => { if(newMessage.trim()) { toast.success("Message sent"); setNewMessage(""); } }}>
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </PortalLayout>
  );
};

export default MessagesNotices;
